
import { Component, Prop, Emit } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  ButtonPrimary,
  NavBar,
  RoundedIcon,
  Row,
  Tag,
  ContextColorProvider
} from '@warrenbrasil/nebraska-web';

import { CloseButton } from '@/modules/common/components/buttons';
import { RouteMixin } from '@/modules/common/mixins/route';
import { StorageKeys } from '@/modules/common/services/local-storage';
import { ICustomer } from '@/types/models';
import { GtmTracker } from '@/modules/common/services/analytics';

import {
  ILegacyUserProfile,
  ILegacyUserProfileRow,
  LegacyUserProfileActionRows
} from '../../types';
import { ModalNewInvestorProfile } from '../ModalNewInvestorProfile';
import { trackBeginInvestment } from '../../helpers/track-events';
import { getMyLegacyUserProfile } from '../../services/api';

const actionRowsLabels = {
  [LegacyUserProfileActionRows.Wealth]: 'carteira',
  [LegacyUserProfileActionRows.Trade]: 'trade',
  [LegacyUserProfileActionRows.Cash]: 'conta_remunerada',
  [LegacyUserProfileActionRows.Dashboard]: 'dashboard'
};

@Component({
  components: {
    ContextColorProvider,
    NavBar,
    CloseButton,
    Row,
    RoundedIcon,
    ButtonPrimary,
    Tag,
    ModalNewInvestorProfile
  }
})
export default class UserProfile extends mixins(RouteMixin) {
  @Prop({ type: Object })
  private user?: ICustomer;

  private NebraskaColors = NebraskaColors;

  public userProfile?: ILegacyUserProfile;
  public loadedUserProfile = false;

  async mounted() {
    this.userProfile = await getMyLegacyUserProfile(this.user!._id!);
    this.loadedUserProfile = true;

    this.$forceUpdate();
    this.$nextTick(() => {
      this.loaded();
    });
  }

  private getRowIcon(iconName: string) {
    let name = '';
    switch (iconName) {
      case 'wealth':
        name = 'ic_wealth';
        break;
      case 'cash':
        name = 'ic_cash';
        break;
      case 'trade':
        name = 'ic_trade';
        break;
      case 'dashboard':
        name = 'ic_overview';
        break;
    }
    return name;
  }

  private getRowIconColor(iconName: string) {
    let color = '';
    switch (iconName) {
      case 'wealth':
        color = NebraskaColors.wealthPrimary;
        break;
      case 'cash':
        color = NebraskaColors.cashPrimary;
        break;
      case 'trade':
        color = NebraskaColors.tradePrimary;
        break;
      case 'dashboard':
        color = NebraskaColors.brandPrimary;
        break;
    }

    return color;
  }

  public rowAction(row: ILegacyUserProfileRow) {
    this.trackActionRowClick(row.type);
    localStorage.setItem(StorageKeys.ProfileViewActionRowType, row.type);
    this.buttonAction(row.action);
  }

  private buttonAction(action: string) {
    switch (action) {
      case 'start_builder':
        return this.goToPath('/app/#/builder');
      case 'start_suitability':
        return this.$router.push({ path: '/suitability' });
      case 'start_register':
        return this.goToRegister();
      case 'start_dashboard':
        return this.goToDash();
      case 'start_update_account_regulation':
        return this.goToEditAccount();
    }
  }

  private onButtonClick() {
    const isImpersonate = localStorage.getItem(StorageKeys.Partner);
    isImpersonate ? this.goToRegister() : this.goToDash();
  }

  private goToDash() {
    this.$router.push({ path: '/v3' });
  }

  private goToRegister() {
    this.goToPath('/app/#/register');
  }

  private goToEditAccount() {
    this.goToPath('/app/#/user/account');
  }

  private trackActionRowClick(type: LegacyUserProfileActionRows) {
    trackBeginInvestment(actionRowsLabels[type]);
    GtmTracker.trackEvent({
      category: 'Perfil de Investidor Action Buttons',
      label: actionRowsLabels[type],
      action: 'Clique'
    });
  }

  get isCustomerPJ() {
    return Boolean(this.user?.professionalProfile);
  }

  get showButton() {
    return (
      this.isCustomerPJ ||
      !this.userProfile?.actionRows ||
      (this.userProfile.actionRows && this.userProfile.actionRows.length === 0)
    );
  }

  get showModalNewInvestorProfile() {
    return Boolean(this.$route.query.isNewInvestorProfile);
  }

  @Emit()
  private loaded() {}

  private get mainButtonAction() {
    return this.userProfile?.actionButton?.action ?? '';
  }
}
